"use client";

import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Text,
  Heading,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Badge,
  Progress,
  Tooltip,
  useDisclosure,
  Grid,
  GridItem,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import SessionOverview from "../charts/SessionOverview";
import SOPDeviationCard from "../metrics/SOPDeviationCard";
import DealSentimentCard from "../metrics/DealSentimentCard";
import CallQualityCard from "../metrics/CallQualityCard";
import MetricsComparisonChart from "../charts/projectmetrics/MetricsComparisonChart";
import MetricsOverview from "../metrics/SingleProject/MetricsOverview";
import { getProjectMetricsData } from "../metrics/SingleProject/projectMetricsData";
import {
  appAnalyticsService,
  AnalyticsParams,
  SessionOverviewData,
  TopObjectionData as ApiTopObjectionData,
  TopQuestionData as ApiTopQuestionData,
  UserSessionData,
  SessionOverTimeData
} from "../../services/appAnalyticsService";
import { 
  hasSingleProjectAccess, 
  getSingleProjectName, 
  getAvailableProjects 
} from "../../utils/auth";
import TimeRangeSelector from "../metrics/SingleProject/TimeRangeSelector";
import dayjs from 'dayjs';
import {
  ConversionProbabilityChart,
  MeetingsPerRepChart,
  TalkListenByRepChart,
  SopByRepChart,
  SopComplianceByRepChart,
  ObjectionHandlingByRepChart,
  TopObjectionCategoriesChart,
  ModuleUsageChart,
  TopPerformersChart,
  LeadTemperatureChart,
  AvgDurationByRepChart,
  SentimentTrendChart,
  PositivityByRepTrendChart,
  ObjectionsTrendChart,
  UnitInterestDonut,
  UnitSizeDemandDonut,
  TopRisksChart,
  TimePerModuleChart,
  MissedModulesHeatmap,
  TechUtilizationTrendChart,
  DealSizeTrendChart,
  BudgetDistributionChart,
  EmotionalUpliftChart,
  TopAmenitiesChart,
  AmenitySentimentChart,
  BuyerIntentPieChart,
  LuxuryIntentChart,
  RevenueAtRiskChart,
} from "../analytics";

// Define interfaces for question and objection data
interface QuestionData {
  question: string;
  percentage: number;
  count: number;
}

interface ObjectionData {
  objection: string;
  count: number;
}

interface ObjectionItem {
  objection_text: string;
  count: number;
}

interface ObjectionCategory {
  category: string;
  count: number;
  percentage: number;
  objections: ObjectionItem[];
}

interface CategorizedObjectionsData {
  categories: ObjectionCategory[];
}

// --- Locally defined types to match original static data structures ---
interface BounceRateData {
  bounceRate: number;
  totalSessions: number;
  bounceSessions: number;
  trend: number;
}

interface FrequencyData {
  averageTimePerSession: number;
  totalUniqueUsers: number;
  trend: number;
}
// --- End of locally defined types ---

// Get available projects based on user access
const getProjectsList = () => {
  const userProjects = getAvailableProjects();
  if (userProjects.length > 0) {
    return userProjects;
  }
  // Fallback to dummy projects if no user projects found
  return [
    "Birla",
    "Dugasta", 
    "Eldeco Edge",
    "M3M Panipat",
    "M3M Cullinan",
    "IC"
  ];
};

// Placeholder mapping (in a real app, this might come from config or an API)
const projectToBusinessIdMap: Record<string, string | undefined> = {
  "Birla": "birla",
  "Dugasta": "dugasta",
  "Eldeco Edge": "eldeco_edge",
  "M3M Panipat": "m3m_panipat",
  "M3M Cullinan": "m3m_cullinan",
  "IC": "ic",
  "All Projects": undefined, // No specific business ID for "All Projects"
};

const projectToProjectIdMap: Record<string, string | undefined> = {
  "Birla": "Birla", // Assuming project names are used as IDs if not specified otherwise
  "Dugasta": "Dugasta",
  "Eldeco Edge": "Eldeco Edge",
  "M3M Panipat": "M3M Panipat",
  "M3M Cullinan": "M3M Cullinan",
  "IC": "IC",
  "All Projects": undefined,
};

// Define interface for analytics data (for project-specific metrics)
interface AnalyticsDataState {
  bounceRate: BounceRateData | null;
  frequency: FrequencyData | null;
  topObjections: ObjectionData[];
  categorizedObjections: CategorizedObjectionsData | null;
}

// Define interface for detailed session data (for SessionAnalytics component)
interface DetailedSessionDataState {
  activeTime: Array<{ hour: string; count: number }> | null;
  userSessions: UserSessionData[] | null;
  sessionOverTime: SessionOverTimeData[] | null;
}

// Project-specific session data for SessionOverview
const projectSessionData: Record<string, SessionOverviewData> = {
  "All Projects": {
    totalSessions: 1342,
    data: [
      { month: "Jan", count: 18 },
      { month: "Feb", count: 22 },
      { month: "Mar", count: 26 },
      { month: "Apr", count: 28 },
      { month: "May", count: 30 },
      { month: "Jun", count: 32 }
    ]
  },
  "Birla": {
    totalSessions: 385,
    data: [
      { month: "Jan", count: 8 },
      { month: "Feb", count: 12 },
      { month: "Mar", count: 14 },
      { month: "Apr", count: 18 },
      { month: "May", count: 21 },
      { month: "Jun", count: 15 }
    ]
  },
  "Dugasta": {
    totalSessions: 256,
    data: [
      { month: "Jan", count: 5 },
      { month: "Feb", count: 8 },
      { month: "Mar", count: 10 },
      { month: "Apr", count: 14 },
      { month: "May", count: 12 },
      { month: "Jun", count: 9 }
    ]
  },
  "Eldeco Edge": {
    totalSessions: 312,
    data: [
      { month: "Jan", count: 6 },
      { month: "Feb", count: 9 },
      { month: "Mar", count: 12 },
      { month: "Apr", count: 15 },
      { month: "May", count: 16 },
      { month: "Jun", count: 10 }
    ]
  },
  "M3M Panipat": {
    totalSessions: 179,
    data: [
      { month: "Jan", count: 4 },
      { month: "Feb", count: 6 },
      { month: "Mar", count: 8 },
      { month: "Apr", count: 9 },
      { month: "May", count: 10 },
      { month: "Jun", count: 8 }
    ]
  },
  "M3M Cullinan": {
    totalSessions: 158,
    data: [
      { month: "Jan", count: 3 },
      { month: "Feb", count: 5 },
      { month: "Mar", count: 7 },
      { month: "Apr", count: 8 },
      { month: "May", count: 9 },
      { month: "Jun", count: 7 }
    ]
  },
  "IC": {
    totalSessions: 52,
    data: [
      { month: "Jan", count: 1 },
      { month: "Feb", count: 2 },
      { month: "Mar", count: 3 },
      { month: "Apr", count: 5 },
      { month: "May", count: 6 },
      { month: "Jun", count: 4 }
    ]
  }
};

// Project-specific session analytics data
const projectAnalyticsData: Record<string, AnalyticsDataState> = {
  "All Projects": {
    bounceRate: { bounceRate: 42.5, totalSessions: 1342, bounceSessions: 531, trend: -2.3 },
    frequency: { averageTimePerSession: 1740, totalUniqueUsers: 951, trend: 8.5 },
    topObjections: [
      { objection: "Too costly", count: 10 },
      { objection: "No urgency", count: 8 },
      { objection: "Need to discuss", count: 6 },
      { objection: "Competition offers", count: 5 },
      { objection: "Location issue", count: 4 }
    ],
    categorizedObjections: null
  },
  "Birla": {
    bounceRate: { bounceRate: 38.2, totalSessions: 385, bounceSessions: 147, trend: -3.4 },
    frequency: { averageTimePerSession: 1920, totalUniqueUsers: 274, trend: 9.8 },
    topObjections: [
      { objection: "Too costly", count: 12 },
      { objection: "Need to discuss", count: 9 },
      { objection: "Location issue", count: 7 },
      { objection: "Better alternatives", count: 6 },
      { objection: "Waiting for approvals", count: 4 }
    ],
    categorizedObjections: null
  },
  "Dugasta": {
    bounceRate: { bounceRate: 44.8, totalSessions: 256, bounceSessions: 115, trend: 1.2 },
    frequency: { averageTimePerSession: 1560, totalUniqueUsers: 193, trend: 5.3 },
    topObjections: [
      { objection: "No urgency", count: 11 },
      { objection: "Too costly", count: 8 },
      { objection: "Competition offers", count: 6 },
      { objection: "Need more information", count: 5 },
      { objection: "Better alternatives", count: 4 }
    ],
    categorizedObjections: null
  },
  "Eldeco Edge": {
    bounceRate: { bounceRate: 35.9, totalSessions: 312, bounceSessions: 112, trend: -4.7 },
    frequency: { averageTimePerSession: 1860, totalUniqueUsers: 218, trend: 10.2 },
    topObjections: [
      { objection: "Location issue", count: 9 },
      { objection: "Need to discuss", count: 7 },
      { objection: "Too costly", count: 6 },
      { objection: "Need more information", count: 5 },
      { objection: "Waiting for approvals", count: 3 }
    ],
    categorizedObjections: null
  },
  "M3M Panipat": {
    bounceRate: { bounceRate: 46.2, totalSessions: 179, bounceSessions: 83, trend: 0.8 },
    frequency: { averageTimePerSession: 1620, totalUniqueUsers: 143, trend: 6.7 },
    topObjections: [
      { objection: "No urgency", count: 8 },
      { objection: "Competition offers", count: 7 },
      { objection: "Location issue", count: 6 },
      { objection: "Too costly", count: 5 },
      { objection: "Need to discuss", count: 3 }
    ],
    categorizedObjections: null
  },
  "M3M Cullinan": {
    bounceRate: { bounceRate: 41.8, totalSessions: 158, bounceSessions: 66, trend: -1.5 },
    frequency: { averageTimePerSession: 1680, totalUniqueUsers: 124, trend: 7.9 },
    topObjections: [
      { objection: "Too costly", count: 9 },
      { objection: "Competition offers", count: 6 },
      { objection: "No urgency", count: 5 },
      { objection: "Better alternatives", count: 4 },
      { objection: "Need more information", count: 3 }
    ],
    categorizedObjections: null
  },
  "IC": {
    bounceRate: { bounceRate: 48.1, totalSessions: 52, bounceSessions: 25, trend: 2.4 },
    frequency: { averageTimePerSession: 1440, totalUniqueUsers: 39, trend: 4.2 },
    topObjections: [
      { objection: "Need more information", count: 7 },
      { objection: "No urgency", count: 6 },
      { objection: "Too costly", count: 5 },
      { objection: "Need to discuss", count: 4 },
      { objection: "Competition offers", count: 3 }
    ],
    categorizedObjections: null
  }
};

// Project-specific detailed session data for SessionAnalytics
const projectDetailedData: Record<string, DetailedSessionDataState> = {
  "All Projects": {
    activeTime: [
      { hour: "00", count: 10 },
      { hour: "01", count: 8 },
      { hour: "02", count: 5 },
      { hour: "03", count: 3 },
      { hour: "04", count: 2 },
      { hour: "05", count: 3 },
      { hour: "06", count: 7 },
      { hour: "07", count: 15 },
      { hour: "08", count: 25 },
      { hour: "09", count: 42 },
      { hour: "10", count: 65 },
      { hour: "11", count: 87 },
      { hour: "12", count: 95 },
      { hour: "13", count: 85 },
      { hour: "14", count: 70 },
      { hour: "15", count: 58 },
      { hour: "16", count: 45 },
      { hour: "17", count: 35 },
      { hour: "18", count: 28 },
      { hour: "19", count: 20 },
      { hour: "20", count: 15 },
      { hour: "21", count: 12 },
      { hour: "22", count: 10 },
      { hour: "23", count: 8 }
    ],
    userSessions: [
      { date: "Mon", newUsers: 20, returningUsers: 35 },
      { date: "Tue", newUsers: 25, returningUsers: 40 },
      { date: "Wed", newUsers: 30, returningUsers: 45 },
      { date: "Thu", newUsers: 40, returningUsers: 50 },
      { date: "Fri", newUsers: 45, returningUsers: 55 },
      { date: "Sat", newUsers: 30, returningUsers: 35 },
      { date: "Sun", newUsers: 20, returningUsers: 25 }
    ],
    sessionOverTime: [
      { date: "2025-02-01", count: 48, duration: 25 },
      { date: "2025-02-02", count: 22, duration: 28 },
      { date: "2025-02-03", count: 55, duration: 20 },
      { date: "2025-02-04", count: 29, duration: 23 },
      { date: "2025-02-05", count: 23, duration: 110 },
      { date: "2025-02-06", count: 40, duration: 15 },
      { date: "2025-02-07", count: 35, duration: 18 },
      { date: "2025-02-08", count: 28, duration: 24 },
      { date: "2025-02-09", count: 30, duration: 26 },
      { date: "2025-02-10", count: 12, duration: 22 },
      { date: "2025-02-11", count: 115, duration: 23 },
      { date: "2025-02-12", count: 53, duration: 28 },
      { date: "2025-02-13", count: 32, duration: 30 },
      { date: "2025-02-14", count: 44, duration: 25 },
      { date: "2025-02-15", count: 36, duration: 21 },
      { date: "2025-02-16", count: 25, duration: 29 }
    ]
  },
  "Birla": {
    activeTime: [
      { hour: "00", count: 5 },
      { hour: "01", count: 4 },
      { hour: "02", count: 2 },
      { hour: "03", count: 1 },
      { hour: "04", count: 1 },
      { hour: "05", count: 2 },
      { hour: "06", count: 3 },
      { hour: "07", count: 8 },
      { hour: "08", count: 15 },
      { hour: "09", count: 25 },
      { hour: "10", count: 35 },
      { hour: "11", count: 45 },
      { hour: "12", count: 50 },
      { hour: "13", count: 45 },
      { hour: "14", count: 38 },
      { hour: "15", count: 30 },
      { hour: "16", count: 25 },
      { hour: "17", count: 18 },
      { hour: "18", count: 15 },
      { hour: "19", count: 10 },
      { hour: "20", count: 8 },
      { hour: "21", count: 6 },
      { hour: "22", count: 5 },
      { hour: "23", count: 4 }
    ],
    userSessions: [
      { date: "Mon", newUsers: 10, returningUsers: 20 },
      { date: "Tue", newUsers: 12, returningUsers: 25 },
      { date: "Wed", newUsers: 15, returningUsers: 30 },
      { date: "Thu", newUsers: 18, returningUsers: 35 },
      { date: "Fri", newUsers: 22, returningUsers: 40 },
      { date: "Sat", newUsers: 15, returningUsers: 25 },
      { date: "Sun", newUsers: 10, returningUsers: 15 }
    ],
    sessionOverTime: [
      { date: "2025-02-01", count: 25, duration: 30 },
      { date: "2025-02-02", count: 15, duration: 32 },
      { date: "2025-02-03", count: 30, duration: 28 },
      { date: "2025-02-04", count: 18, duration: 26 },
      { date: "2025-02-05", count: 14, duration: 32 },
      { date: "2025-02-06", count: 22, duration: 29 },
      { date: "2025-02-07", count: 19, duration: 31 },
      { date: "2025-02-08", count: 15, duration: 28 },
      { date: "2025-02-09", count: 18, duration: 32 },
      { date: "2025-02-10", count: 8, duration: 27 },
      { date: "2025-02-11", count: 65, duration: 30 },
      { date: "2025-02-12", count: 30, duration: 33 },
      { date: "2025-02-13", count: 20, duration: 35 },
      { date: "2025-02-14", count: 25, duration: 30 },
      { date: "2025-02-15", count: 20, duration: 28 },
      { date: "2025-02-16", count: 15, duration: 32 }
    ]
  },
  "Dugasta": {
    activeTime: [
      { hour: "00", count: 3 },
      { hour: "01", count: 2 },
      { hour: "02", count: 1 },
      { hour: "03", count: 1 },
      { hour: "04", count: 0 },
      { hour: "05", count: 1 },
      { hour: "06", count: 2 },
      { hour: "07", count: 5 },
      { hour: "08", count: 10 },
      { hour: "09", count: 18 },
      { hour: "10", count: 25 },
      { hour: "11", count: 32 },
      { hour: "12", count: 35 },
      { hour: "13", count: 30 },
      { hour: "14", count: 25 },
      { hour: "15", count: 20 },
      { hour: "16", count: 15 },
      { hour: "17", count: 12 },
      { hour: "18", count: 10 },
      { hour: "19", count: 8 },
      { hour: "20", count: 5 },
      { hour: "21", count: 4 },
      { hour: "22", count: 3 },
      { hour: "23", count: 2 }
    ],
    userSessions: [
      { date: "Mon", newUsers: 8, returningUsers: 15 },
      { date: "Tue", newUsers: 10, returningUsers: 18 },
      { date: "Wed", newUsers: 12, returningUsers: 22 },
      { date: "Thu", newUsers: 15, returningUsers: 25 },
      { date: "Fri", newUsers: 18, returningUsers: 28 },
      { date: "Sat", newUsers: 12, returningUsers: 18 },
      { date: "Sun", newUsers: 8, returningUsers: 12 }
    ],
    sessionOverTime: [
      { date: "2025-02-01", count: 18, duration: 26 },
      { date: "2025-02-02", count: 10, duration: 28 },
      { date: "2025-02-03", count: 22, duration: 25 },
      { date: "2025-02-04", count: 12, duration: 22 },
      { date: "2025-02-05", count: 10, duration: 28 },
      { date: "2025-02-06", count: 15, duration: 24 },
      { date: "2025-02-07", count: 14, duration: 26 },
      { date: "2025-02-08", count: 12, duration: 23 },
      { date: "2025-02-09", count: 15, duration: 28 },
      { date: "2025-02-10", count: 6, duration: 22 },
      { date: "2025-02-11", count: 48, duration: 25 },
      { date: "2025-02-12", count: 22, duration: 28 },
      { date: "2025-02-13", count: 15, duration: 30 },
      { date: "2025-02-14", count: 18, duration: 26 },
      { date: "2025-02-15", count: 14, duration: 24 },
      { date: "2025-02-16", count: 10, duration: 26 }
    ]
  }
};

// Add project-specific top questions data
const projectQuestionsData: Record<string, QuestionData[]> = {
  "All Projects": [
    { question: "Location and Connectivity", percentage: 30, count: 111 },
    { question: "Project Details", percentage: 25, count: 89 },
    { question: "Pricing and Costs", percentage: 15, count: 35 },
    { question: "Amenities and Facilities", percentage: 10, count: 31 },
    { question: "Other", percentage: 20, count: 62 }
  ],
  "Birla": [
    { question: "Location and Connectivity", percentage: 38, count: 65 },
    { question: "Project Details", percentage: 22, count: 42 },
    { question: "Pricing and Costs", percentage: 18, count: 31 },
    { question: "Payment Plans", percentage: 12, count: 21 },
    { question: "Other", percentage: 10, count: 17 }
  ],
  "Dugasta": [
    { question: "Pricing and Costs", percentage: 35, count: 48 },
    { question: "Location and Connectivity", percentage: 25, count: 34 },
    { question: "Project Timeline", percentage: 18, count: 24 },
    { question: "Amenities and Facilities", percentage: 12, count: 16 },
    { question: "Other", percentage: 10, count: 14 }
  ],
  "Eldeco Edge": [
    { question: "Project Details", percentage: 32, count: 52 },
    { question: "Amenities and Facilities", percentage: 28, count: 45 },
    { question: "Pricing and Costs", percentage: 20, count: 32 },
    { question: "Location and Connectivity", percentage: 12, count: 19 },
    { question: "Other", percentage: 8, count: 13 }
  ],
  "M3M Panipat": [
    { question: "Project Timeline", percentage: 30, count: 28 },
    { question: "Pricing and Costs", percentage: 25, count: 23 },
    { question: "Location and Connectivity", percentage: 20, count: 19 },
    { question: "Payment Plans", percentage: 15, count: 14 },
    { question: "Other", percentage: 10, count: 9 }
  ],
  "M3M Cullinan": [
    { question: "Amenities and Facilities", percentage: 35, count: 31 },
    { question: "Pricing and Costs", percentage: 30, count: 27 },
    { question: "Project Details", percentage: 15, count: 13 },
    { question: "Location and Connectivity", percentage: 12, count: 11 },
    { question: "Other", percentage: 8, count: 7 }
  ],
  "IC": [
    { question: "Pricing and Costs", percentage: 40, count: 12 },
    { question: "Payment Plans", percentage: 25, count: 8 },
    { question: "Project Timeline", percentage: 20, count: 6 },
    { question: "Location and Connectivity", percentage: 10, count: 3 },
    { question: "Other", percentage: 5, count: 2 }
  ]
};

// Define CONVRSE_BUSINESS_ID_FOR_ALL_PROJECTS once at the module level
const CONVRSE_BUSINESS_ID_FOR_ALL_PROJECTS = "convrse_main_business_id_placeholder";

const Dashboard: React.FC = () => {
  // Check if user has single project access
  const isSingleProject = hasSingleProjectAccess();
  const singleProjectName = getSingleProjectName();
  
  // Initialize selected project based on access level
  const [selectedProject, setSelectedProject] = useState(() => {
    if (isSingleProject && singleProjectName) {
      return singleProjectName;
    }
    return "All Projects";
  });
  
  // Add global time range state
  const [timeRange, setTimeRange] = useState('7d');
  
  const [selectedMetric, setSelectedMetric] = useState("Call Quality");
  const { isOpen: isObjectionsOpen, onOpen: onObjectionsOpen, onClose: onObjectionsClose } = useDisclosure();
  const { isOpen: isQuestionsOpen, onOpen: onQuestionsOpen, onClose: onQuestionsClose } = useDisclosure();

  // --- Corrected State Declarations ---
  const [sessionOverview, setSessionOverview] = useState<SessionOverviewData | null>(null);
  const [isLoadingSessionOverview, setIsLoadingSessionOverview] = useState(false);
  const [errorSessionOverview, setErrorSessionOverview] = useState<string | null>(null);

  const [analyticsData, setAnalyticsData] = useState<AnalyticsDataState>({
    bounceRate: null,
    frequency: null,
    topObjections: [],
    categorizedObjections: null,
  });
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [errorAnalytics, setErrorAnalytics] = useState<string | null>(null);
  
  const [detailedSessionData, setDetailedSessionData] = useState<DetailedSessionDataState>({
    activeTime: null,
    userSessions: null,
    sessionOverTime: null,
  });
  const [isLoadingDetailedSession, setIsLoadingDetailedSession] = useState(false);
  const [errorDetailedSession, setErrorDetailedSession] = useState<string | null>(null);

  const [topQuestions, setTopQuestions] = useState<QuestionData[]>([]);
  const [isLoadingTopQuestions, setIsLoadingTopQuestions] = useState(false);
  const [errorTopQuestions, setErrorTopQuestions] = useState<string | null>(null);
  
  // State for effective IDs to be used by child components that need them
  const businessIdentifier = localStorage.getItem('business_identifier') || CONVRSE_BUSINESS_ID_FOR_ALL_PROJECTS;
  const [effectiveBusinessId, setEffectiveBusinessId] = useState<string | undefined>(businessIdentifier);
  const [effectiveProjectId, setEffectiveProjectId] = useState<string | undefined>(projectToProjectIdMap[selectedProject]);
  // --- End of Corrected State Declarations ---

  const projectMetricsData = getProjectMetricsData(selectedProject);
  
  // Helper function to convert time range to date range
  const getDateRangeFromTimeRange = (timeRange: string) => {
    const endDate = dayjs().format('YYYY-MM-DD');
    let startDate: string;
    
    switch (timeRange) {
      case '7d':
        startDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
        break;
      case '30d':
        startDate = dayjs().subtract(30, 'days').format('YYYY-MM-DD');
        break;
      case '90d':
        startDate = dayjs().subtract(90, 'days').format('YYYY-MM-DD');
        break;
      default:
        startDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
    }
    
    return { startDate, endDate };
  };
  
  useEffect(() => {
    const currentProjectSelection = selectedProject;
    let newEffectiveProjectId = projectToProjectIdMap[currentProjectSelection];
    // let newEffectiveBusinessId = projectToBusinessIdMap[currentProjectSelection];
    let newEffectiveBusinessId = localStorage.getItem('business_identifier') || projectToBusinessIdMap[currentProjectSelection];

    // Logic for CONVRSE_BUSINESS_ID_FOR_ALL_PROJECTS
    if (newEffectiveBusinessId === CONVRSE_BUSINESS_ID_FOR_ALL_PROJECTS) {
      newEffectiveProjectId = undefined; 
    } else if (currentProjectSelection === "All Projects") {
      newEffectiveProjectId = undefined;
      newEffectiveBusinessId = undefined; 
    }
    
    setEffectiveBusinessId(newEffectiveBusinessId);
    setEffectiveProjectId(newEffectiveProjectId);

    // Get date range for API calls
    const { startDate, endDate } = getDateRangeFromTimeRange(timeRange);

    const params: AnalyticsParams = { 
      projectId: newEffectiveProjectId,
      businessId: newEffectiveBusinessId,
      type: 'real_estate',
      startDate,
      endDate
    };    

    // Fetch Session Overview
    const fetchSessionOverview = async () => {
      setIsLoadingSessionOverview(true);
      setErrorSessionOverview(null);
      try {
        const data = await appAnalyticsService.getSessionOverview(params);
        setSessionOverview(data);
      } catch (err: any) {
        setErrorSessionOverview(err.message || "Failed to fetch session overview");
      } finally {
        setIsLoadingSessionOverview(false);
      }
    };

    // Fetch Analytics Data (Bounce Rate, Frequency, Top Objections)
    const fetchAnalytics = async () => {
      setIsLoadingAnalytics(true);
      setErrorAnalytics(null);
      try {
        const [bounceData, freqData, objectionsData] = await Promise.all([
          appAnalyticsService.getBounceRate(params),
          appAnalyticsService.getFrequency(params),
          appAnalyticsService.getTopObjectionsForProject(params)
        ]);
        
        console.log('Objections API Response:', objectionsData);
        
        // Handle new categorized structure
        let categorizedObjections: CategorizedObjectionsData | null = null;
        let topObjections: ObjectionData[] = [];
        
        // Check if the response has the new categorized structure
        if (objectionsData && typeof objectionsData === 'object' && !Array.isArray(objectionsData) && 'categories' in objectionsData) {
          categorizedObjections = objectionsData as unknown as CategorizedObjectionsData;
          console.log('Categorized objections found:', categorizedObjections);
          // Flatten objections from first category for backward compatibility
          if (categorizedObjections.categories && categorizedObjections.categories.length > 0) {
            topObjections = categorizedObjections.categories[0].objections.map(o => ({
              objection: o.objection_text,
              count: o.count
            }));
            console.log('Flattened objections from first category:', topObjections);
          }
        } else if (Array.isArray(objectionsData)) {
          // Fallback to old structure
          topObjections = objectionsData.map((o: any) => ({ objection: o.objection, count: o.count }));
          console.log('Using old structure, objections:', topObjections);
        } else {
          console.log('Unknown objections data structure:', objectionsData);
        }
        
        console.log('Final topObjections:', topObjections);
        console.log('Final categorizedObjections:', categorizedObjections);
        
        setAnalyticsData({
          bounceRate: bounceData as BounceRateData,
          frequency: freqData as FrequencyData,
          topObjections,
          categorizedObjections,
        });
      } catch (err: any) {
        setErrorAnalytics(err.message || "Failed to fetch analytics data");
        setAnalyticsData({ bounceRate: null, frequency: null, topObjections: [], categorizedObjections: null });
      } finally {
        setIsLoadingAnalytics(false);
      }
    };

    // Fetch Detailed Session Data (Active Time, User Sessions, Session Over Time)
    const fetchDetailedSession = async () => {
      setIsLoadingDetailedSession(true);
      setErrorDetailedSession(null);
      try {
        const [activeData, /* userSessionsData */ sessionOverTimeDataFromApi] = await Promise.all([
          appAnalyticsService.getActiveTime(params),
          // appAnalyticsService.getUserSessions(params), // Endpoint /api/dashboard/user-sessions does not exist
          appAnalyticsService.getSessionOverTime(params)
        ]);
        setDetailedSessionData({
          activeTime: activeData,
          userSessions: null, // Set to null as it's no longer fetched
          sessionOverTime: sessionOverTimeDataFromApi,
        });
      } catch (err: any) {
        setErrorDetailedSession(err.message || "Failed to fetch detailed session data");
      } finally {
        setIsLoadingDetailedSession(false);
      }
    };
    
    // Fetch Top Questions
    const fetchTopQuestionsData = async () => {
      setIsLoadingTopQuestions(true);
      setErrorTopQuestions(null);
      try {
        const data = await appAnalyticsService.getTopQuestionsForProject(params);
        setTopQuestions(data.map(q => ({
            question: q.question,
            count: q.count,
            percentage: q.percentage
        })));
      } catch (err: any) {
        setErrorTopQuestions(err.message || "Failed to fetch top questions");
      } finally {
        setIsLoadingTopQuestions(false);
      }
    };

    fetchSessionOverview();
    fetchAnalytics();
    fetchDetailedSession();
    fetchTopQuestionsData();
  }, [selectedProject, timeRange, effectiveBusinessId, effectiveProjectId]);

  const handleMetricSelect = (metric: string) => {
    if (selectedMetric === metric) return;
    setSelectedMetric(metric);
  };

  // Get project-specific session and analytics data
  // const displaySessionData = sessionOverview || { totalSessions: 0, data: [] }; // Original line using incorrect sessionOverview
  
  // Prepare data for SessionOverview from detailedSessionData.sessionOverTime
  const sessionOverTimeForChart = detailedSessionData.sessionOverTime || [];
  const totalSessionsForChart = sessionOverTimeForChart.reduce((sum, item) => sum + item.count, 0);
  const dataForSessionOverviewChart = sessionOverTimeForChart.map(item => ({
    month: new Date(item.date).toLocaleString('default', { month: 'short' }), // Convert 'YYYY-MM-DD' to 'Mon'
    count: item.count
  }));
  // Group by month and sum counts if multiple entries for the same month (e.g. daily data over several months)
  const aggregatedDataForSessionOverviewChart = Object.values(
    dataForSessionOverviewChart.reduce((acc, { month, count }) => {
      acc[month] = (acc[month] || { month, count: 0 });
      acc[month].count += count;
      return acc;
    }, {} as Record<string, { month: string; count: number }>)
  );


  const displayAnalyticsData = {
    bounceRate: {
      bounceRate: analyticsData.bounceRate?.bounceRate || 0,
      totalSessions: analyticsData.bounceRate?.totalSessions || 0,
      bounceSessions: analyticsData.bounceRate?.bounceSessions || 0,
      trend: (analyticsData.bounceRate?.trend !== undefined && !isNaN(analyticsData.bounceRate.trend)) ? analyticsData.bounceRate.trend : 0
    },
    frequency: {
      averageTimePerSession: analyticsData.frequency?.averageTimePerSession || 0,
      totalUniqueUsers: analyticsData.frequency?.totalUniqueUsers || 0,
      trend: (analyticsData.frequency?.trend !== undefined && !isNaN(analyticsData.frequency.trend)) ? analyticsData.frequency.trend : 0
    },
    topObjections: analyticsData.topObjections.length > 0 ? analyticsData.topObjections : [],
    categorizedObjections: analyticsData.categorizedObjections,
  };
  const displayDetailedData = {
      activeTime: detailedSessionData.activeTime || [],
      userSessions: [], // Ensure this is an empty array or handle its absence in rendering
      sessionOverTime: detailedSessionData.sessionOverTime || []
  };
  const displayTopQuestions = topQuestions.length > 0 ? topQuestions : [];

  const totalUniqueUsers = displayAnalyticsData.frequency.totalUniqueUsers;
  const avgTime = Math.floor(displayAnalyticsData.frequency.averageTimePerSession / 60);

  // Function to convert milliseconds to readable time format
  const formatDuration = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return {
        value: hours,
        unit: hours === 1 ? 'hour' : 'hours',
        subValue: remainingMinutes > 0 ? remainingMinutes : null,
        subUnit: remainingMinutes === 1 ? 'min' : 'mins'
      };
    } else if (minutes > 0) {
      return {
        value: minutes,
        unit: minutes === 1 ? 'min' : 'mins',
        subValue: remainingSeconds > 0 ? remainingSeconds : null,
        subUnit: remainingSeconds === 1 ? 'sec' : 'secs'
      };
    } else {
      return {
        value: seconds,
        unit: seconds === 1 ? 'sec' : 'secs',
        subValue: null,
        subUnit: null
      };
    }
  };

  const avgTimeFormatted = formatDuration(displayAnalyticsData.frequency.averageTimePerSession);

  const handleProjectSelect = (project: string) => {
    setSelectedProject(project);
    setSelectedMetric("Call Quality"); // Reset selected metric for comparison chart
  };
  
  const isOverallLoading = isLoadingSessionOverview || isLoadingAnalytics || isLoadingDetailedSession || isLoadingTopQuestions;

  const TopQuestionsCard = () => (
    <Box p={5} shadow="md" borderWidth="1px" borderRadius="md" minH="220px">
      <Heading size="sm" mb={4}>Top Questions</Heading>
      {isLoadingTopQuestions && <Spinner />}
      {errorTopQuestions && <Text color="red.500">{errorTopQuestions}</Text>}
      {!isLoadingTopQuestions && !errorTopQuestions && displayTopQuestions.length === 0 && <Text>No questions data available.</Text>}
      {!isLoadingTopQuestions && !errorTopQuestions && displayTopQuestions.slice(0, 3).map((item, index) => (
        <Box key={index} mb={2}>
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" noOfLines={1} title={item.question}>{item.question}</Text>
            <Badge colorScheme={item.count > 8 ? "red" : item.count > 5 ? "orange" : "green"} fontSize="xs">
              {item.count}
            </Badge>
          </Flex>
          <Progress value={(item.count / 10) * 100} size="xs" colorScheme={item.count > 8 ? "red" : item.count > 5 ? "orange" : "green"} mt={1} />
        </Box>
      ))}
      {displayTopQuestions.length > 3 && (
        <Flex justify="flex-end">
          <Text 
            fontSize="sm" 
            color="blue.500" 
            cursor="pointer" 
            onClick={onQuestionsOpen}
            mt={2}
          >
            +{displayTopQuestions.length - 3} more questions
          </Text>
        </Flex>
      )}
    </Box>
  );

  const TopObjectionsCard = () => {
    // Get categories to display
    const getDisplayCategories = () => {
      if (displayAnalyticsData.categorizedObjections?.categories && displayAnalyticsData.categorizedObjections.categories.length > 0) {
        return displayAnalyticsData.categorizedObjections.categories.map(cat => ({
          category: cat.category,
          count: cat.count,
          percentage: cat.percentage
        }));
      }
      return [];
    };

    const displayCategories = getDisplayCategories();
    const totalCategories = displayCategories.length;
    const maxCount = displayCategories.length > 0 ? Math.max(...displayCategories.map(c => c.count)) : 0;
    
    // Check if there's only one category and it's "Uncategorized"
    const isOnlyUncategorized = totalCategories === 1 && displayCategories[0]?.category?.toLowerCase() === 'uncategorized';
    
    // Helper function to get display name for category
    const getCategoryDisplayName = (categoryName: string) => {
      if (isOnlyUncategorized) {
        return 'Objections';
      }
      return categoryName;
    };

    return (
      <Box p={5} shadow="md" borderWidth="1px" borderRadius="md" minH="220px">
        <Heading size="sm" mb={4}>Top Objections</Heading>
        {isLoadingAnalytics && <Spinner />}
        {errorAnalytics && <Text color="red.500">{errorAnalytics}</Text>}
        {!isLoadingAnalytics && !errorAnalytics && displayCategories.length === 0 && <Text>No objections data available.</Text>}
        {!isLoadingAnalytics && !errorAnalytics && displayCategories.slice(0, 3).map((item, index) => {
          return (
            <Box key={index} mb={2}>
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" noOfLines={1} title={item.category}>{getCategoryDisplayName(item.category)}</Text>
                <Badge colorScheme={item.count > 8 ? "red" : item.count > 5 ? "orange" : "green"} fontSize="xs">
                  {item.count}
                </Badge>
              </Flex>
              <Progress 
                value={maxCount > 0 ? (item.count / maxCount) * 100 : 0} 
                size="xs" 
                colorScheme={item.count > 8 ? "red" : item.count > 5 ? "orange" : "green"} 
                mt={1} 
              />
            </Box>
          );
        })}
        {totalCategories > 3 ? (
          <Flex justify="flex-end">
            <Text 
              fontSize="sm" 
              color="blue.500" 
              cursor="pointer" 
              onClick={onObjectionsOpen}
              mt={2}
            >
              +{totalCategories - 3} more categories
            </Text>
          </Flex>
        ) : (
          <Flex justify="flex-end">
          <Text 
            fontSize="sm" 
            color="blue.500" 
            cursor="pointer" 
            onClick={onObjectionsOpen}
            mt={2}
          >
            show objections
          </Text>
        </Flex>
        )}
      </Box>
    );
  };

  return (
    <Box as="section" bg="gray.50" minH="100vh">
      <Box as="header" px={8} py={6} bg="white" borderBottomWidth="1px">
        <Flex justify="space-between" align="center">
          <Heading
            as="h1"
            fontSize="32px"
            fontWeight="600"
            color="gray.800"
          >
            DASHBOARD
          </Heading>
          <Flex align="center" gap={4}>
            {/* Global Time Range Selector */}
            <TimeRangeSelector 
              value={timeRange} 
              onChange={setTimeRange} 
            />
            
            {!isSingleProject && (
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.200"
                  _hover={{ bg: "gray.50" }}
                  _active={{ bg: "gray.50" }}
                  width="170px"
                  textAlign="left"
                  fontSize="sm"
                  isDisabled={isOverallLoading}
                >
                  {selectedProject}
                </MenuButton>
                <MenuList maxH="300px" overflowY="auto">
                  <MenuItem onClick={() => handleProjectSelect("All Projects")}>All Projects</MenuItem>
                  {getProjectsList().map((project: string, idx: number) => (
                    <MenuItem key={idx} onClick={() => handleProjectSelect(project)}>
                      {project}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {isSingleProject && (
              <Box
                bg="white"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={4}
                py={2}
                fontSize="sm"
                color="gray.800"
                fontWeight="medium"
              >
                {selectedProject}
              </Box>
            )}
          </Flex>
        </Flex>
      </Box>

      <Container maxW="container.xl" px={6} py={6}>
        <Box mb={6} display="flex" gap={6} flexWrap="wrap">
          <Box flex="2" minW={{base: "100%", md: "66%"}} display="flex" flexDirection="column" gap={6}>
            <Flex gap={6} direction={{base: "column", md: "row"}}>
              <Box 
                bg="white" 
                borderRadius="lg" 
                p={5} 
                boxShadow="sm" 
                h="120px"
                flex="1"
              >
                {isLoadingAnalytics ? <Spinner size="sm"/> : errorAnalytics ? <Text color="red.500" fontSize="xs">Error</Text> : 
                <>
                  <Text fontSize="md" color="gray.600" mb={1}>Total Unique Users</Text>
                  <Flex align="flex-end">
                    <Text fontSize="5xl" lineHeight="1" mb={1} fontWeight="normal" color="gray.800">
                      {totalUniqueUsers}
                    </Text>
                    <Flex ml="auto" flexDir="column" align="flex-end" mb={3}>
                      {displayAnalyticsData.frequency.trend !== 0 ? (
                        <>
                          <Flex color={displayAnalyticsData.frequency.trend > 0 ? "green.600" : "red.600"} fontWeight="medium" align="center">
                            <Text>{displayAnalyticsData.frequency.trend > 0 ? "up" : "down"} by {Math.abs(displayAnalyticsData.frequency.trend)}% </Text>
                            <Box as="span" ml={1}>{displayAnalyticsData.frequency.trend > 0 ? "↑" : "↓"}</Box>
                          </Flex>
                          <Text fontSize="xs" color="gray.500">From last month average</Text>
                        </>
                      ) : (
                        <Text fontSize="xs" color="gray.500">No trend data available</Text>
                      )}
                    </Flex>
                  </Flex>
                </>}
              </Box>

              <Box 
                bg="white" 
                borderRadius="lg" 
                p={5} 
                boxShadow="sm"
                h="120px"
                flex="1"
              >
                {isLoadingAnalytics ? <Spinner size="sm"/> : errorAnalytics ? <Text color="red.500" fontSize="xs">Error</Text> : 
                <>
                  <Text fontSize="md" color="gray.600" mb={1}>Average Time per session</Text>
                  <Flex align="flex-end">
                    <Flex align="baseline">
                      <Text fontSize="5xl" lineHeight="1" fontWeight="normal" color="gray.800">
                        {avgTimeFormatted.value}
                      </Text>
                      <Text fontSize="xl" mb={1.5} ml={1} color="gray.800">{avgTimeFormatted.unit}</Text>
                      {avgTimeFormatted.subValue && (
                        <>
                          <Text fontSize="lg" mb={1.5} ml={2} color="gray.600">
                            {avgTimeFormatted.subValue}
                          </Text>
                          <Text fontSize="lg" mb={1.5} ml={1} color="gray.600">{avgTimeFormatted.subUnit}</Text>
                        </>
                      )}
                    </Flex>
                    <Flex ml="auto" flexDir="column" align="flex-end" mb={3}>
                      {displayAnalyticsData.frequency.trend !== 0 ? (
                        <>
                          <Flex color={displayAnalyticsData.frequency.trend > 0 ? "green.600" : "red.600"} fontWeight="medium" align="center">
                            <Text>{displayAnalyticsData.frequency.trend > 0 ? "up" : "down"} by {Math.abs(displayAnalyticsData.frequency.trend)}% </Text>
                            <Box as="span" ml={1}>{displayAnalyticsData.frequency.trend > 0 ? "↑" : "↓"}</Box>
                          </Flex>
                          <Text fontSize="xs" color="gray.500">From last month average</Text>
                        </>
                      ) : (
                        <Text fontSize="xs" color="gray.500">No trend data available</Text>
                      )}
                    </Flex>
                  </Flex>
                </>}
              </Box>
            </Flex>

            <Box
              bg="white"
              borderRadius="lg"
              p={5}
              boxShadow="sm"
              flex="1"
              minH="300px"
            >
              {isLoadingDetailedSession && <Flex justifyContent="center" alignItems="center" h="100%"><Spinner /></Flex>}
              {errorDetailedSession && <Text color="red.500">{errorDetailedSession}</Text>}
              {!isLoadingDetailedSession && !errorDetailedSession && detailedSessionData.sessionOverTime && (
                <SessionOverview 
                  title={`Sessions Overview: ${selectedProject}`}
                  totalSessions={totalSessionsForChart}
                  data={aggregatedDataForSessionOverviewChart}
                />
              )}
              {!isLoadingDetailedSession && !errorDetailedSession && !detailedSessionData.sessionOverTime && <Text>No session overview data available.</Text>}
            </Box>
          </Box>

          <Box flex="1" minW={{base: "100%", md: "300px"}} display="flex" flexDirection="column" gap={6}>
            <Box h={{base: "auto", md: "calc(50% - 3px)"}} minH="220px">
              <TopQuestionsCard />
            </Box>

            <Box h={{base: "auto", md: "calc(50% - 3px)"}} minH="220px">
              <TopObjectionsCard />
            </Box>
          </Box>
        </Box>

        <Box mt={8}>
          {!isSingleProject && selectedProject === "All Projects" ? (
            <>
              <Flex justify="space-between" gap={6} flexWrap={{base: "wrap", md: "nowrap"}} mb={6}>
                <SOPDeviationCard 
                  onMetricSelect={handleMetricSelect} 
                  businessIdentifier={effectiveBusinessId}
                  timeRange={timeRange}
                />
                <DealSentimentCard 
                  onMetricSelect={handleMetricSelect} 
                  businessIdentifier={effectiveBusinessId}
                  timeRange={timeRange}
                />
                <CallQualityCard 
                  onMetricSelect={handleMetricSelect} 
                  businessIdentifier={effectiveBusinessId}
                  timeRange={timeRange}
                />
              </Flex>
              
              <Box mt={6}>
                <Box flex="1" minW="0">
                  <Heading size="lg" mb={6} textAlign="center">Project Performance Dashboard</Heading>
                  <MetricsComparisonChart 
                    title="Metrics Comparison" 
                    selectedMetrics={[selectedMetric]}
                    onMetricSelect={handleMetricSelect}
                    effectiveBusinessId={effectiveBusinessId}
                    effectiveProjectId={effectiveProjectId}
                  />
                </Box>
              </Box>
            </>
          ) : isSingleProject ? (
            <>
              {/* Single Project View - Only show SingleProject MetricsOverview */}
              <Box mb={4}>
                {/* <Heading size="md" mb={4}>{selectedProject} Metrics</Heading> */}
                <MetricsOverview 
                  businessIdentifier={effectiveBusinessId}
                  projectData={projectMetricsData}
                  timeRange={timeRange}
                />
              </Box>
            </>
          ) : (
            <>
              {/* Multi-project user with specific project selected */}
              <Flex justify="space-between" gap={6} flexWrap={{base: "wrap", md: "nowrap"}} mb={6}>
                <SOPDeviationCard 
                  onMetricSelect={handleMetricSelect} 
                  businessIdentifier={effectiveBusinessId}
                  timeRange={timeRange}
                />
                <DealSentimentCard 
                  onMetricSelect={handleMetricSelect} 
                  businessIdentifier={effectiveBusinessId}
                  timeRange={timeRange}
                />
                <CallQualityCard 
                  onMetricSelect={handleMetricSelect} 
                  businessIdentifier={effectiveBusinessId}
                  timeRange={timeRange}
                />
              </Flex>
              
              <Box mb={4}>
                <Heading size="md" mb={4}>{selectedProject} Metrics</Heading>
                <MetricsOverview 
                  businessIdentifier={effectiveBusinessId}
                  projectData={projectMetricsData}
                  timeRange={timeRange}
                />
              </Box>
            </>
          )}
        </Box>

        {/* ========== Analytics Charts Section ========== */}
        <Box mt={10} mb={6}>
          <Heading as="h2" fontSize="24px" fontWeight="600" color="gray.800" mb={6}>
            Analytics
          </Heading>

          {/* --- Conversion Analytics --- */}
          <Heading as="h3" fontSize="18px" fontWeight="500" color="gray.700" mb={4}>
            Conversion Analytics
          </Heading>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
            <ConversionProbabilityChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <LeadTemperatureChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <UnitInterestDonut
              businessIdentifier={effectiveBusinessId}
            />
            <UnitSizeDemandDonut
              businessIdentifier={effectiveBusinessId}
            />
            <DealSizeTrendChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <BudgetDistributionChart
              businessIdentifier={effectiveBusinessId}
            />
          </SimpleGrid>

          {/* --- Salesperson Performance --- */}
          <Heading as="h3" fontSize="18px" fontWeight="500" color="gray.700" mb={4}>
            Salesperson Performance
          </Heading>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
            <MeetingsPerRepChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <TalkListenByRepChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <SopByRepChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <SopComplianceByRepChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <ObjectionHandlingByRepChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <AvgDurationByRepChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <PositivityByRepTrendChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
          </SimpleGrid>

          {/* --- Objection & Risk Analytics --- */}
          <Heading as="h3" fontSize="18px" fontWeight="500" color="gray.700" mb={4}>
            Objection & Risk Analytics
          </Heading>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
            <TopObjectionCategoriesChart
              data={
                displayAnalyticsData.categorizedObjections?.categories
                  ? displayAnalyticsData.categorizedObjections.categories.map(cat => ({
                      category: cat.category,
                      count: cat.count,
                      percentage: cat.percentage,
                    }))
                  : []
              }
            />
            <ObjectionsTrendChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <TopRisksChart
              businessIdentifier={effectiveBusinessId}
            />
          </SimpleGrid>

          {/* --- Emotional & Behavioral Intelligence --- */}
          <Heading as="h3" fontSize="18px" fontWeight="500" color="gray.700" mb={4}>
            Emotional & Behavioral Intelligence
          </Heading>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
            <SentimentTrendChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <EmotionalUpliftChart
              businessIdentifier={effectiveBusinessId}
            />
            <BuyerIntentPieChart
              businessIdentifier={effectiveBusinessId}
            />
            <LuxuryIntentChart
              businessIdentifier={effectiveBusinessId}
            />
          </SimpleGrid>

          {/* --- Marketing & Product Insights --- */}
          <Heading as="h3" fontSize="18px" fontWeight="500" color="gray.700" mb={4}>
            Marketing & Product Insights
          </Heading>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
            <TopAmenitiesChart
              businessIdentifier={effectiveBusinessId}
            />
            <AmenitySentimentChart
              businessIdentifier={effectiveBusinessId}
            />
          </SimpleGrid>

          {/* --- Experience Center Analytics --- */}
          <Heading as="h3" fontSize="18px" fontWeight="500" color="gray.700" mb={4}>
            Experience Center Analytics
          </Heading>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
            <ModuleUsageChart
              projectId={effectiveProjectId}
              businessId={effectiveBusinessId}
            />
            <TimePerModuleChart
              projectId={effectiveProjectId}
              businessId={effectiveBusinessId}
            />
            <TechUtilizationTrendChart
              projectId={effectiveProjectId}
              businessId={effectiveBusinessId}
              timeRange={timeRange}
            />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1 }} spacing={6} mb={8}>
            <MissedModulesHeatmap
              projectId={effectiveProjectId}
              businessId={effectiveBusinessId}
            />
          </SimpleGrid>

          {/* --- Executive Summary --- */}
          <Heading as="h3" fontSize="18px" fontWeight="500" color="gray.700" mb={4}>
            Executive Summary
          </Heading>
          <SimpleGrid columns={{ base: 1 }} spacing={6} mb={8}>
            <TopPerformersChart
              businessIdentifier={effectiveBusinessId}
              timeRange={timeRange}
            />
            <RevenueAtRiskChart
              businessIdentifier={effectiveBusinessId}
            />
          </SimpleGrid>
        </Box>
      </Container>

      {/* Modal for all objections */}
      <Modal isOpen={isObjectionsOpen} onClose={onObjectionsClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(5px)" />
        <ModalContent maxW="60vw">
          <ModalHeader>All Customer Objections</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="60vh" overflowY="auto" position="relative">
            {displayAnalyticsData.categorizedObjections?.categories && displayAnalyticsData.categorizedObjections.categories.length > 0 ? (
              (() => {
                const categories = displayAnalyticsData.categorizedObjections.categories;
                const isOnlyUncategorized = categories.length === 1 && categories[0]?.category?.toLowerCase() === 'uncategorized';
                const getCategoryDisplayName = (categoryName: string) => {
                  if (isOnlyUncategorized) {
                    return 'Objection';
                  }
                  return categoryName;
                };
                return (
                  <Tabs>
                    <Box 
                      overflowX="auto" 
                      mb={4}
                      position="sticky"
                      top={-5}
                      bg="white"
                      zIndex={10}
                      pt={2}
                      pb={2}
                      // borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      <TabList flexWrap="nowrap" minW="max-content">
                        {categories.map((category, index) => (
                          <Tab key={index} whiteSpace="nowrap">
                            {getCategoryDisplayName(category.category)} ({category.count})
                          </Tab>
                        ))}
                      </TabList>
                    </Box>
                    <TabPanels>
                      {categories.map((category, categoryIndex) => (
                        <TabPanel key={categoryIndex} px={0}>
                          <Box overflowX="auto">
                            <Table variant="simple" size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Objection</Th>
                                  <Th isNumeric>Count</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {category.objections.map((item, index) => (
                                  <Tr key={index}>
                                    <Td maxW="400px" wordBreak="break-word">{item.objection_text}</Td>
                                    <Td isNumeric>
                                      <Badge
                                        colorScheme={
                                          item.count > 8 ? "red" :
                                          item.count > 5 ? "orange" :
                                          "green"
                                        }
                                        variant="subtle"
                                      >
                                        {item.count}
                                      </Badge>
                                    </Td>
                                  </Tr>
                                ))}
                                {category.objections.length === 0 && (
                                  <Tr><Td colSpan={2} textAlign="center">No objections in this category.</Td></Tr>
                                )}
                              </Tbody>
                            </Table>
                          </Box>
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>
                );
              })()
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Objection</Th>
                      <Th isNumeric>Count</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {displayAnalyticsData.topObjections.map((item: ObjectionData, index: number) => (
                      <Tr key={index}>
                        <Td maxW="400px" wordBreak="break-word">{item.objection}</Td>
                        <Td isNumeric>
                          <Badge
                            colorScheme={
                              item.count > 8 ? "red" :
                              item.count > 5 ? "orange" :
                              "green"
                            }
                            variant="subtle"
                          >
                            {item.count}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                    {displayAnalyticsData.topObjections.length === 0 && (
                      <Tr><Td colSpan={2} textAlign="center">No objections data available.</Td></Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onObjectionsClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal for all questions */}
      <Modal isOpen={isQuestionsOpen} onClose={onQuestionsClose} size="lg">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(5px)" />
        <ModalContent>
          <ModalHeader>All Top Questions</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Question</Th>
                  <Th isNumeric>Count</Th>
                  <Th>Percentage</Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayTopQuestions.map((item: QuestionData, index: number) => (
                  <Tr key={index}>
                    <Td>{item.question}</Td>
                    <Td isNumeric>{item.count}</Td>
                    <Td>
                      <Badge
                        colorScheme="purple"
                        variant="subtle"
                      >
                        {item.percentage}%
                      </Badge>
                    </Td>
                  </Tr>
                ))}
                {displayTopQuestions.length === 0 && (
                    <Tr><Td colSpan={3} textAlign="center">No questions data available.</Td></Tr>
                )}
              </Tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onQuestionsClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Dashboard; 